import { arrayValue, isArray } from '../array.js';
import type * as Core from '../../types/calculation-core.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
function productMetadata(calculatorId: string, notes: readonly string[], version: string = '1.0.0', effectiveFrom: string = '2026-08-20'): Core.CoreMetadata {
    return {
        calculatorId,
        version,
        effectiveFrom,
        lastVerifiedAt: effectiveFrom,
        sources: [],
        notes,
    };
}
export const LPG_PAYBACK_NOTES = Object.freeze([
    'Fiyat, tüketim, kilometre ve masraf değerlerinin tamamı kullanıcı girdisidir; piyasa fiyatı varsayılmaz.',
    'Sonuç teknik uygunluk veya tasarruf garantisi değildir.',
]);
export const FUEL_CONSUMPTION_NOTES = Object.freeze([
    'Tüketim ve litre fiyatı kullanıcı girdisidir; yakıt türüne göre otomatik fiyat veya tüketim atanmaz.',
]);
export const PROFIT_MARGIN_NOTES = Object.freeze([
    'Satış marjı kârın satış fiyatına, kâr ekleme oranı ise kârın maliyete oranıdır; bu iki yüzde aynı değildir.',
    'Vergi, komisyon ve diğer giderler kullanıcı tarafından maliyete dahil edilmelidir.',
]);
export const MIN_STABLE_POSITIVE_INPUT = 1e-9;
export const MIN_STABLE_MONETARY_INPUT = 0.01;
export function finiteOutput<T>(value: T, numbers: readonly number[], metadata: Core.CoreMetadata) {
    return numbers.every(Number.isFinite)
        ? success(value, metadata)
        : failure([{
                field: 'result',
                code: 'CALCULATION_OVERFLOW',
                message: 'Sonuç sonlu sayı aralığını aştı.',
            }], metadata);
}
function boundedNumber(field: string, value: unknown, minimum: number, maximum: number) {
    if (typeof value !== 'number' || !isFiniteNumber(value)) {
        return { field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` };
    }
    if (value < minimum || value > maximum) {
        return { field, code: 'OUT_OF_RANGE', message: `${field} izin verilen aralığın dışındadır.` };
    }
    return undefined;
}
export function productMetadataErrors(metadata: Core.CoreMetadata, calculatorId: string, requiredNotes: readonly string[]): Core.InputError[] {
    const errors = [];
    if (metadata?.calculatorId !== calculatorId) {
        errors.push({ field: 'config.metadata.calculatorId', code: 'INVALID_CONFIG', message: 'Hesap motoru kimliği değiştirilemez.' });
    }
    if (!isArray(metadata?.sources) || metadata.sources.length !== 0) {
        errors.push({ field: 'config.metadata.sources', code: 'INVALID_CONFIG', message: 'Bu ürün motoru uzaktan kaynak metadatası kabul etmez.' });
    }
    const notes = metadata?.notes;
    if (notes === undefined || !isArray(notes)
        || notes.length !== requiredNotes.length
        || notes.some((note: string, index) => note !== requiredNotes[index])) {
        errors.push({ field: 'config.metadata.notes', code: 'INVALID_CONFIG', message: 'Ürün varsayımı ve kapsam uyarıları eksik bırakılamaz.' });
    }
    return errors;
}
export const DEFAULT_LPG_PAYBACK_CONFIG = {
    metadata: productMetadata('lpg-payback', LPG_PAYBACK_NOTES),
    maxAbsoluteInput: 1000000000,
};
export function simpleConfigErrors(config: Core.LpgPaybackConfig, calculatorId: string, requiredNotes: readonly string[]): Core.InputError[] {
    const errors = [
        ...validateMetadata(config.metadata),
        ...productMetadataErrors(config.metadata, calculatorId, requiredNotes),
    ];
    if (!isFiniteNumber(config.maxAbsoluteInput) || config.maxAbsoluteInput <= 0) {
        errors.push({ field: 'config.maxAbsoluteInput', code: 'INVALID_CONFIG', message: 'Azami giriş sınırı pozitif ve sonlu olmalıdır.' });
    }
    return errors;
}
export function calculateLpgPayback(input: Core.LpgPaybackInput, config: Core.LpgPaybackConfig = DEFAULT_LPG_PAYBACK_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_LPG_PAYBACK_CONFIG.metadata);
    if (!config || typeof config !== 'object') {
        return failure([{ field: 'config', code: 'INVALID_CONFIG', message: 'LPG amortisman konfigürasyonu geçersiz.' }], metadata);
    }
    const configErrors = simpleConfigErrors(config, 'lpg-payback', LPG_PAYBACK_NOTES);
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'LPG amortisman girdileri gereklidir.' }], metadata);
    }
    const errors = [];
    for (const field of [
        'conversionCost',
        'gasolineConsumptionL100Km',
        'lpgConsumptionL100Km',
        'gasolinePricePerLitre',
        'lpgPricePerLitre',
        'monthlyDistanceKm',
    ] as const) {
        const error = boundedNumber(field, input[field], MIN_STABLE_POSITIVE_INPUT, config.maxAbsoluteInput);
        if (error)
            errors.push(error);
    }
    const extraOneOffCost = input.extraOneOffCost ?? 0;
    const extraError = boundedNumber('extraOneOffCost', extraOneOffCost, 0, config.maxAbsoluteInput);
    if (extraError)
        errors.push(extraError);
    if (errors.length > 0)
        return failure(errors, metadata);
    const gasolineCostPerKm = input.gasolinePricePerLitre * input.gasolineConsumptionL100Km / 100;
    const lpgCostPerKm = input.lpgPricePerLitre * input.lpgConsumptionL100Km / 100;
    const savingsPerKm = gasolineCostPerKm - lpgCostPerKm;
    const investment = input.conversionCost + extraOneOffCost;
    if (gasolineCostPerKm <= 0 || lpgCostPerKm <= 0 || investment <= 0) {
        return failure([{
                field: 'result',
                code: 'CALCULATION_OVERFLOW',
                message: 'Girdi ölçeği güvenilir bir LPG amortisman sonucu üretmek için çok küçüktür.',
            }], metadata);
    }
    const monthlySavings = savingsPerKm * input.monthlyDistanceKm;
    const annualSavings = monthlySavings * 12;
    const paybackPossible = savingsPerKm > 0;
    const paybackDistanceKm = paybackPossible ? investment / savingsPerKm : null;
    const paybackMonths = paybackDistanceKm === null ? null : paybackDistanceKm / input.monthlyDistanceKm;
    if (paybackPossible
        && (savingsPerKm * 100 <= 0 || monthlySavings <= 0 || annualSavings <= 0)) {
        return failure([{
                field: 'result',
                code: 'CALCULATION_OVERFLOW',
                message: 'Girdi ölçeği güvenilir bir LPG tasarruf sonucu üretmek için çok küçüktür.',
            }], metadata);
    }
    const numbers = [
        gasolineCostPerKm,
        lpgCostPerKm,
        savingsPerKm,
        investment,
        monthlySavings,
        annualSavings,
        ...(paybackDistanceKm === null || paybackMonths === null ? [] : [paybackDistanceKm, paybackMonths]),
    ];
    return finiteOutput({
        ...input,
        extraOneOffCost,
        gasolineCostPerKm,
        lpgCostPerKm,
        savingsPerKm,
        savingsPer100Km: savingsPerKm * 100,
        investment,
        monthlySavings,
        annualSavings,
        paybackPossible,
        paybackDistanceKm,
        paybackMonths,
    }, [...numbers, savingsPerKm * 100], metadata);
}
export const DEFAULT_FUEL_CONSUMPTION_CONFIG: Core.FuelConsumptionConfig = {
    metadata: productMetadata('fuel-consumption', FUEL_CONSUMPTION_NOTES),
    distanceKm: [1, 20000],
    consumptionL100Km: [1, 50],
    pricePerLitre: [1, 500],
};
export function validateRangeConfig(field: string, bounds: unknown, minimumAllowed: number) {
    const entries = arrayValue(bounds);
    const range = entries !== null && entries.length === 2 ? entries : null;
    const minimum = range?.[0];
    const maximum = typeof minimum === 'number' && isFiniteNumber(minimum) ? range?.[1] : undefined;
    return typeof minimum === 'number' && isFiniteNumber(minimum)
        && typeof maximum === 'number' && isFiniteNumber(maximum)
        && minimum <= maximum
        && minimum >= minimumAllowed
        ? undefined
        : { field, code: 'INVALID_CONFIG', message: 'Girdi sınırı geçersiz.' };
}
export function calculateFuelConsumption(input: Core.FuelConsumptionInput, config: Core.FuelConsumptionConfig = DEFAULT_FUEL_CONSUMPTION_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_FUEL_CONSUMPTION_CONFIG.metadata);
    if (!config || typeof config !== 'object') {
        return failure([{ field: 'config', code: 'INVALID_CONFIG', message: 'Yakıt tüketimi konfigürasyonu geçersiz.' }], metadata);
    }
    const configErrors = [...validateMetadata(config.metadata)];
    configErrors.push(...productMetadataErrors(config.metadata, 'fuel-consumption', FUEL_CONSUMPTION_NOTES));
    for (const field of ['distanceKm', 'consumptionL100Km', 'pricePerLitre'] as const) {
        const error = validateRangeConfig(`config.${field}`, config[field], MIN_STABLE_POSITIVE_INPUT);
        if (error)
            configErrors.push(error);
    }
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Yakıt tüketimi girdileri gereklidir.' }], metadata);
    }
    const errors = [];
    const distanceError = boundedNumber('distanceKm', input.distanceKm, ...config.distanceKm);
    const consumptionError = boundedNumber('consumptionL100Km', input.consumptionL100Km, ...config.consumptionL100Km);
    const priceError = boundedNumber('pricePerLitre', input.pricePerLitre, ...config.pricePerLitre);
    if (distanceError)
        errors.push(distanceError);
    if (consumptionError)
        errors.push(consumptionError);
    if (priceError)
        errors.push(priceError);
    if (!['benzin', 'motorin', 'lpg'].includes(input.fuelType)) {
        errors.push({ field: 'fuelType', code: 'UNSUPPORTED_VALUE', message: 'Yakıt türü desteklenmiyor.' });
    }
    if (input.direction !== 'tekYon' && input.direction !== 'gidisDonus') {
        errors.push({ field: 'direction', code: 'UNSUPPORTED_VALUE', message: 'Yol yönü desteklenmiyor.' });
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const effectiveDistanceKm = input.direction === 'gidisDonus' ? input.distanceKm * 2 : input.distanceKm;
    const consumedLitres = effectiveDistanceKm * input.consumptionL100Km / 100;
    const costPer100Km = input.consumptionL100Km * input.pricePerLitre;
    const costPerKm = costPer100Km / 100;
    const totalCost = consumedLitres * input.pricePerLitre;
    if (effectiveDistanceKm <= 0
        || consumedLitres <= 0
        || costPerKm <= 0
        || costPer100Km <= 0
        || totalCost <= 0) {
        return failure([{
                field: 'result',
                code: 'CALCULATION_OVERFLOW',
                message: 'Girdi ölçeği güvenilir bir yakıt tüketimi sonucu üretmek için çok küçüktür.',
            }], metadata);
    }
    return finiteOutput({
        ...input,
        effectiveDistanceKm,
        consumedLitres,
        costPerKm,
        costPer100Km,
        totalCost,
    }, [effectiveDistanceKm, consumedLitres, costPerKm, costPer100Km, totalCost], metadata);
}
export const DEFAULT_PROFIT_MARGIN_CONFIG: Core.ProfitMarginConfig = {
    metadata: productMetadata('profit-margin', PROFIT_MARGIN_NOTES, '2.0.0', '2026-08-24'),
    monetaryInput: [MIN_STABLE_MONETARY_INPUT, 1000000000],
    targetMarginPercent: [0, 99.99],
    markupPercent: [0, 1000000],
};
export function calculateProfitMargin(input: Readonly<Core.ProfitMarginInput>, config: Core.ProfitMarginConfig = DEFAULT_PROFIT_MARGIN_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_PROFIT_MARGIN_CONFIG.metadata);
    if (!config || typeof config !== 'object') {
        return failure([{ field: 'config', code: 'INVALID_CONFIG', message: 'Kâr marjı konfigürasyonu geçersiz.' }], metadata);
    }
    const configErrors = [
        ...validateMetadata(config.metadata),
        ...productMetadataErrors(config.metadata, 'profit-margin', PROFIT_MARGIN_NOTES),
    ];
    for (const [field, bounds, minimumAllowed] of [
        ['monetaryInput', config.monetaryInput, MIN_STABLE_MONETARY_INPUT],
        ['targetMarginPercent', config.targetMarginPercent, 0],
        ['markupPercent', config.markupPercent, 0],
    ] as const) {
        const error = validateRangeConfig(`config.${field}`, bounds, minimumAllowed);
        if (error)
            configErrors.push(error);
    }
    if (isArray(config.targetMarginPercent) &&
        config.targetMarginPercent.length === 2 &&
        isFiniteNumber(config.targetMarginPercent[1]) &&
        config.targetMarginPercent[1] >= 100) {
        configErrors.push({
            field: 'config.targetMarginPercent',
            code: 'INVALID_CONFIG',
            message: 'Hedef satış marjı üst sınırı yüzde 100 değerinden küçük olmalıdır.',
        });
    }
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Kâr marjı girdileri gereklidir.' }], metadata);
    }
    const record = input;
    const mode = record.mode;
    const errors = [];
    const costError = boundedNumber('cost', record.cost, ...config.monetaryInput);
    if (costError)
        errors.push(costError);
    if (mode !== 'targetSaleMargin' && mode !== 'costMarkup' && mode !== 'analyzeSale') {
        errors.push({ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'Kâr marjı hesap türü desteklenmiyor.' });
    }
    const activeField = mode === 'targetSaleMargin'
        ? 'targetMarginPercent'
        : mode === 'costMarkup'
            ? 'markupPercent'
            : mode === 'analyzeSale'
                ? 'salePrice'
                : null;
    const allowedFields = activeField === null ? ['mode', 'cost'] : ['mode', 'cost', activeField];
    for (const field of Object.keys(record)) {
        if (!allowedFields.includes(field)) {
            errors.push({
                field,
                code: 'UNSUPPORTED_VALUE',
                message: 'Seçilen hesap türü bu alanı kabul etmiyor.',
            });
        }
    }
    if (activeField !== null && !Object.prototype.hasOwnProperty.call(record, activeField)) {
        errors.push({ field: activeField, code: 'REQUIRED', message: 'Seçilen hesap türü için bu değer gereklidir.' });
    }
    else if (mode === 'targetSaleMargin') {
        const error = boundedNumber('targetMarginPercent', record.targetMarginPercent, ...config.targetMarginPercent);
        if (error)
            errors.push(error);
    }
    else if (mode === 'costMarkup') {
        const error = boundedNumber('markupPercent', record.markupPercent, ...config.markupPercent);
        if (error)
            errors.push(error);
    }
    else if (mode === 'analyzeSale') {
        const error = boundedNumber('salePrice', record.salePrice, ...config.monetaryInput);
        if (error)
            errors.push(error);
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const validatedMode = mode;
    const cost = record.cost;
    const salePrice = mode === 'targetSaleMargin'
        ? cost / (1 - record.targetMarginPercent / 100)
        : mode === 'costMarkup'
            ? cost * (1 + record.markupPercent / 100)
            : record.salePrice;
    const profit = salePrice - cost;
    const requestedPositiveRate = mode === 'targetSaleMargin'
        ? record.targetMarginPercent
        : mode === 'costMarkup'
            ? record.markupPercent
            : 0;
    if (requestedPositiveRate > 0 && profit === 0) {
        return failure([{
                field: 'result',
                code: 'CALCULATION_OVERFLOW',
                message: 'Girdi ölçeği güvenilir bir kâr marjı sonucu üretmek için çok küçüktür.',
            }], metadata);
    }
    const markupPercent = profit / cost * 100;
    const marginPercent = profit / salePrice * 100;
    return finiteOutput({
        mode: validatedMode,
        cost,
        salePrice,
        profit,
        markupPercent,
        marginPercent,
        isLoss: profit < 0,
    }, [salePrice, profit, markupPercent, marginPercent], metadata);
}
